/**
 * Preview do anexo aberto pelo link "Ver arquivo" do Step 4.
 *
 * O `POST /documents` devolve só a S3 key, sem URL pública, então o preview usa
 * o próprio `File` que o titular acabou de escolher, via object URL. Quem cria e
 * revoga essas URLs é o Step 4 — aqui só se exibe o que for recebido.
 */

import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import styled from "styled-components";

import { Modal } from "v2/components/Modal";

import { COLORS, MOBILE_MEDIA_QUERY } from "../tokens";
import { Row, SectionLabel } from "../styled";

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 60vh;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  background-color: ${COLORS.neutralSoft};
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  background-color: ${COLORS.neutralSoft};
`;

const Fallback = styled.p`
  margin: 0;
  padding: 24px;
  text-align: center;
  color: ${COLORS.inkMuted};
  font-size: 14px;
  border: 1px dashed ${COLORS.borderStrong};
  border-radius: 8px;
`;

const FileName = styled(SectionLabel)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

interface IProps {
  open: boolean;
  fileName: string;
  /** Object URL do arquivo local; vazio quando o preview não está disponível. */
  objectUrl: string;
  mimeType: string;
  onClose: () => void;
}

export function DocumentPreviewModal({
  open,
  fileName,
  objectUrl,
  mimeType,
  onClose,
}: IProps) {
  const isImage = mimeType.startsWith("image/");
  const isPdf = mimeType === "application/pdf";

  return (
    <Modal
      open={open}
      size="lg"
    >
      <Row
        $justify="space-between"
        $gap="8px"
      >
        <FileName>{fileName}</FileName>
        <IconButton
          onClick={onClose}
          aria-label="Fechar preview"
        >
          <CloseIcon />
        </IconButton>
      </Row>

      <div style={{ marginTop: "16px" }}>
        {!objectUrl && (
          <Fallback>
            Pré-visualização indisponível para este arquivo. O anexo já foi
            enviado.
          </Fallback>
        )}
        {!!objectUrl && isImage && (
          <PreviewImage
            src={objectUrl}
            alt={fileName}
          />
        )}
        {!!objectUrl && isPdf && (
          <PreviewFrame
            src={objectUrl}
            title={fileName}
          />
        )}
        {!!objectUrl && !isImage && !isPdf && (
          <Fallback>
            Não é possível pré-visualizar este formato. O anexo já foi enviado.
          </Fallback>
        )}
      </div>
    </Modal>
  );
}
